import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { pl } from "date-fns/locale/pl";

import "react-datepicker/dist/react-datepicker.css";
import Courses from "./Courses";
import Users from "./Users";
import { validateAttributes, errorMessage } from "../utils/validations";
import { validations } from "./validations";
import initials from "../utils/initials";

const SelectedClient = (props) => {
  const { clients, setClients, selectedClient, allCourses } = props;
  registerLocale("pl", pl);

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState("");
  const [userLimit, setUserLimit] = useState("");
  const [validTo, setValidTo] = useState("");

  useEffect(() => {
    if (selectedClient) {
      setEmail(selectedClient.email);
      setUserLimit(selectedClient.user_limit);
      setValidTo(new Date(selectedClient.client_valid_to));
    }
  }, [selectedClient]);

  const handleEmailChange = async () => {
    if (email === selectedClient.email) return;

    const validation = validateAttributes(
      {
        email,
      },
      validations,
    );

    setErrors(validation);
    if (validation) return;

    const response = await patchClient({ email: email });

    if (response.ok) {
      setErrors({});
      const data = await response.json();

      setClients(
        clients.map((client) =>
          client.id == selectedClient.id ? data.client : client,
        ),
      );
    } else {
      console.log("error");
    }
  };

  const handleUserLimitChange = async () => {
    if (userLimit === selectedClient.user_limit) return;

    const validation = validateAttributes(
      {
        userLimit,
      },
      validations,
    );

    setErrors(validation);
    if (validation) return;

    const newUserLimit = parseInt(userLimit);
    setUserLimit(newUserLimit);

    const response = await patchClient({ user_limit: newUserLimit });

    if (response.ok) {
      const data = await response.json();
      setClients(
        clients.map((client) =>
          client.id == selectedClient.id ? data.client : client,
        ),
      );
    } else {
      console.log("Error Updating User Limit");
    }
  };

  const handleValidToChange = async (value) => {
    const validation = validateAttributes(
      {
        validTo: value,
      },
      validations,
    );

    setErrors(validation);
    if (validation) return;

    setValidTo(value);

    const response = await patchClient({ client_valid_to: value });

    if (response.ok) {
      const data = await response.json();
      setClients(
        clients.map((client) =>
          client.id == selectedClient.id ? data.client : client,
        ),
      );
    } else {
      console.log("Error Updating Valid To");
    }
  };

  const patchClient = async (params) => {
    return await fetch("/clients/" + selectedClient.id, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        client: params,
      }),
    });
  };

  const className = (attribute) => {
    let className = "flex-1 bg-gray-100 px-3 py-2";
    if (errors && errors[attribute]) {
      className += " border border-red-500 focus:ring-red-500";
    }
    return className;
  };

  return (
    <>
      {selectedClient && (
        <div
          className="p-6 bg-white font-sans text-sm text-gray-800 w-3/4"
          style={{ maxWidth: "1000px" }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-semibold">
              {initials(selectedClient.name)}
            </div>
            <h1 className="text-lg font-semibold">{selectedClient.name}</h1>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex gap-4">
              <span className="w-48 text-gray-500 flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 1 0-2.636 6.364M16.5 12V8.25"
                  />
                </svg>
                Email
              </span>
              <input
                className={className("email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlurCapture={handleEmailChange}
              />
            </div>
            {errorMessage(errors, "email")}

            <div className="flex gap-4">
              <span className="w-48 text-gray-500 flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
                  />
                </svg>
                Limit Użytkowników
              </span>
              <input
                className={className("userLimit")}
                value={userLimit}
                onChange={(e) => setUserLimit(e.target.value)}
                onBlurCapture={handleUserLimitChange}
                type="number"
              />
            </div>
            {errorMessage(errors, "userLimit")}
            <div className="flex gap-4">
              <span className="w-48 text-gray-500 flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3M4 11h16M4 19h16M4 15h16"
                  />
                </svg>
                Aktywne do
              </span>
              <DatePicker
                className={className("validTo")}
                selected={validTo}
                onChange={(value) => handleValidToChange(value)}
                locale="pl"
                dateFormat={"dd.MM.yyyy"}
              />
              {errorMessage(errors, "validTo")}
            </div>
          </div>
          <Users client={selectedClient} />
          <Courses client={selectedClient} allCourses={allCourses} />
        </div>
      )}
    </>
  );
};

export default SelectedClient;

SelectedClient.propTypes = {
  selectedClient: PropTypes.object,
  clients: PropTypes.array,
  allCourses: PropTypes.array,
  setClients: PropTypes.func,
};
